import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, CatalogImageUploadField } from '@/components/common';
import { COLOR_PRESETS } from '@/data/mockCatalog';
import {
  useFundamentals,
  getFundamentalName,
  getHabitName,
  getAttitudeName,
  useCreateFundamental,
  useUpdateFundamental,
  useDeleteFundamental,
  useCreateDailyHabit,
  useUpdateDailyHabit,
  useDeleteDailyHabit,
  useCreateAttitude,
  useUpdateAttitude,
  useDeleteAttitude,
  useMoveFundamental,
  useMoveAttitude,
  useMoveDailyHabit,
  useUploadFundamentalImage,
  useUploadAttitudeImage,
  useUploadDailyHabitImage,
} from '@/hooks/useFundamentals';
import { IconPickerModal } from '@/views/AdminView/SurveyBuilderView/components/IconPickerModal';
import { BUILT_IN_ICONS } from '@/views/AdminView/SurveyBuilderView/data';
import { LangBar, LANG_OPTIONS } from '../NotificationsTab/LangBar';
import type { NotifLang } from '@/types/admin.types';
import { MadurezEditor } from '../../components/MadurezEditor';
import { NivelesAdopcionEditor } from '../../components/NivelesAdopcionEditor';

const LANG_KEYS = LANG_OPTIONS.map(l => l.id) as NotifLang[];
const EMPTY_NAMES: Record<NotifLang, string> = { es: '', en: '', pt: '', fr: '', de: '', nl: '', ro: '' };

type MobileStage = 'funds' | 'mid' | 'detail';

interface DeleteTarget { id: string; name: string; fundamentalId?: string; }

interface FundFormState {
  open: boolean;
  editId: string | null;
  names: Record<NotifLang, string>;
  shortNames: Record<NotifLang, string>;
  descriptions: Record<NotifLang, string>;
  activeLang: NotifLang;
  icon: string;
  color: string;
  url: string;
}

interface BehFormState {
  open: boolean;
  editId: string | null;
  names: Record<NotifLang, string>;
  activeLang: NotifLang;
  fund: string;
}

interface AttFormState {
  open: boolean;
  editId: string | null;
  names: Record<NotifLang, string>;
  descriptions: Record<NotifLang, string>;
  activeLang: NotifLang;
}

const FUND_FORM_DEFAULT: FundFormState = {
  open: false,
  editId: null,
  names: { ...EMPTY_NAMES },
  shortNames: { ...EMPTY_NAMES },
  descriptions: { ...EMPTY_NAMES },
  activeLang: 'es',
  icon: '',
  color: '#342F82',
  url: '',
};
const BEH_FORM_DEFAULT: BehFormState = { open: false, editId: null, names: { ...EMPTY_NAMES }, activeLang: 'es', fund: '' };
const ATT_FORM_DEFAULT: AttFormState = { open: false, editId: null, names: { ...EMPTY_NAMES }, descriptions: { ...EMPTY_NAMES }, activeLang: 'es' };

export function CatalogoSubTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const { data: fundamentals = [], isLoading: loadingFundamentals } = useFundamentals();
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [selectedAttitude, setSelectedAttitude] = useState<string>('');
  const [mobileStage, setMobileStage] = useState<MobileStage>('funds');
  const [fundSearch, setFundSearch] = useState('');
  const [behSearch, setBehSearch] = useState('');
  const [fundForm, setFundForm] = useState<FundFormState>(FUND_FORM_DEFAULT);
  const [showFundIconPicker, setShowFundIconPicker] = useState(false);
  const [behForm, setBehForm] = useState<BehFormState>(BEH_FORM_DEFAULT);
  const [attForm, setAttForm] = useState<AttFormState>(ATT_FORM_DEFAULT);
  const [deleteFund, setDeleteFund] = useState<DeleteTarget | null>(null);
  const [deleteBeh, setDeleteBeh] = useState<DeleteTarget | null>(null);
  const [deleteAtt, setDeleteAtt] = useState<DeleteTarget | null>(null);

  const createFund = useCreateFundamental();
  const updateFund = useUpdateFundamental();
  const deleteFundMut = useDeleteFundamental();
  const createHabit = useCreateDailyHabit();
  const updateHabit = useUpdateDailyHabit();
  const deleteHabitMut = useDeleteDailyHabit();
  const createAtt = useCreateAttitude();
  const updateAtt = useUpdateAttitude();
  const deleteAttMut = useDeleteAttitude();
  const moveFund = useMoveFundamental();
  const moveAtt = useMoveAttitude();
  const moveHabit = useMoveDailyHabit();
  const uploadFundImage = useUploadFundamentalImage();
  const uploadAttImage = useUploadAttitudeImage();
  const uploadHabitImage = useUploadDailyHabitImage();

  const sortedFunds = [...fundamentals].sort((a, b) => a.order - b.order);
  const effectiveSelected = selectedFund || sortedFunds[0]?.id || '';

  const filteredFunds = sortedFunds.filter(f =>
    getFundamentalName(f, lang).toLowerCase().includes(fundSearch.toLowerCase())
  );
  const currentFund = fundamentals.find(f => f.id === effectiveSelected);
  const hasAttitudes = (currentFund?.attitudes.length ?? 0) > 0;
  const sortedAttitudes = [...(currentFund?.attitudes ?? [])].sort((a, b) => a.order - b.order);

  const directHabits = (currentFund?.dailyHabits ?? [])
    .filter(h => !h.attitudeBehaviorId)
    .sort((a, b) => a.order - b.order);
  const filteredDirectHabits = directHabits.filter(b =>
    getHabitName(b, lang).toLowerCase().includes(behSearch.toLowerCase())
  );

  // No auto-selección del primer Attitude: a diferencia del Fundamental, el detalle
  // debe permanecer vacío hasta que el usuario elija explícitamente, para no duplicar
  // su nombre en la lista media y en el encabezado del panel de detalle simultáneamente.
  const effectiveSelectedAttitude = selectedAttitude;
  const currentAttitude = currentFund?.attitudes.find(a => a.id === effectiveSelectedAttitude);
  const attitudeHabits = (currentFund?.dailyHabits ?? [])
    .filter(h => h.attitudeBehaviorId === effectiveSelectedAttitude)
    .sort((a, b) => a.order - b.order);

  function openFundForm(editId: string | null = null) {
    if (editId) {
      const f = fundamentals.find(x => x.id === editId);
      if (f) {
        const existingNames = LANG_KEYS.reduce((acc, l) => { acc[l] = f.names[l] ?? ''; return acc; }, { ...EMPTY_NAMES });
        const existingShortNames = LANG_KEYS.reduce((acc, l) => { acc[l] = f.shortNames?.[l] ?? ''; return acc; }, { ...EMPTY_NAMES });
        const existingDescriptions = LANG_KEYS.reduce((acc, l) => { acc[l] = f.descriptions?.[l] ?? ''; return acc; }, { ...EMPTY_NAMES });
        setFundForm({ open: true, editId, names: existingNames, shortNames: existingShortNames, descriptions: existingDescriptions, activeLang: 'es', icon: f.icon ?? '', color: f.color ?? '#342F82', url: f.url ?? '' });
        return;
      }
    }
    setFundForm({ ...FUND_FORM_DEFAULT, open: true });
  }

  function openBehForm(editId: string | null = null) {
    if (editId) {
      const b = (currentFund?.dailyHabits ?? []).find(x => x.id === editId);
      if (b) {
        const existingNames = LANG_KEYS.reduce((acc, l) => {
          acc[l] = b.names[l] ?? '';
          return acc;
        }, { ...EMPTY_NAMES });
        setBehForm({ open: true, editId, names: existingNames, activeLang: 'es', fund: effectiveSelected });
        return;
      }
    }
    setBehForm({ ...BEH_FORM_DEFAULT, open: true, fund: effectiveSelected });
  }

  function openAttForm(editId: string | null = null) {
    if (editId) {
      const a = (currentFund?.attitudes ?? []).find(x => x.id === editId);
      if (a) {
        const existingNames = LANG_KEYS.reduce((acc, l) => { acc[l] = a.names[l] ?? ''; return acc; }, { ...EMPTY_NAMES });
        const existingDescriptions = LANG_KEYS.reduce((acc, l) => { acc[l] = a.descriptions?.[l] ?? ''; return acc; }, { ...EMPTY_NAMES });
        setAttForm({ open: true, editId, names: existingNames, descriptions: existingDescriptions, activeLang: 'es' });
        return;
      }
    }
    setAttForm({ ...ATT_FORM_DEFAULT, open: true });
  }

  const fundNamesAllFilled = LANG_KEYS.every(l => fundForm.names[l].trim() !== '');
  const fundShortNamesAllFilled = LANG_KEYS.every(l => fundForm.shortNames[l].trim() !== '');
  const fundDescriptionsAllFilled = LANG_KEYS.every(l => fundForm.descriptions[l].trim() !== '');
  const allFundLangsFilled = fundNamesAllFilled && fundShortNamesAllFilled && fundDescriptionsAllFilled;

  function handleSaveFund() {
    if (!allFundLangsFilled) return;
    const names = Object.fromEntries(LANG_KEYS.map(l => [l, fundForm.names[l].trim()]));
    const shortNames = Object.fromEntries(LANG_KEYS.map(l => [l, fundForm.shortNames[l].trim()]));
    const descriptions = Object.fromEntries(LANG_KEYS.map(l => [l, fundForm.descriptions[l].trim()]));
    const commonFields = { names, shortNames, descriptions, icon: fundForm.icon || undefined, color: fundForm.color, url: fundForm.url.trim() || undefined };

    if (fundForm.editId) {
      updateFund.mutate(
        { id: fundForm.editId, ...commonFields },
        { onSuccess: () => setFundForm(f => ({ ...f, open: false })) }
      );
    } else {
      createFund.mutate(commonFields, {
        onSuccess: () => setFundForm(f => ({ ...f, open: false })),
      });
    }
  }

  const allLangsFilled = LANG_KEYS.every(l => behForm.names[l].trim() !== '');

  function handleSaveBeh() {
    if (!behForm.fund || !allLangsFilled) return;
    const names: Record<string, string> = Object.fromEntries(
      LANG_KEYS.map(l => [l, behForm.names[l].trim()])
    );

    if (behForm.editId) {
      const existing = (currentFund?.dailyHabits ?? []).find(h => h.id === behForm.editId);
      const mergedNames = { ...(existing?.names ?? {}), ...names };
      updateHabit.mutate(
        { fundamentalId: behForm.fund, habitId: behForm.editId, names: mergedNames },
        { onSuccess: () => setBehForm(f => ({ ...f, open: false })) }
      );
    } else {
      createHabit.mutate(
        { fundamentalId: behForm.fund, names, attitudeBehaviorId: hasAttitudes ? effectiveSelectedAttitude : undefined },
        { onSuccess: () => setBehForm(f => ({ ...f, open: false })) }
      );
    }
  }

  const attAllLangsFilled = LANG_KEYS.every(l => attForm.names[l].trim() !== '');

  function handleSaveAtt() {
    if (!attAllLangsFilled || !currentFund) return;
    const names: Record<string, string> = Object.fromEntries(LANG_KEYS.map(l => [l, attForm.names[l].trim()]));
    const descriptions: Record<string, string> = Object.fromEntries(
      LANG_KEYS.map(l => [l, attForm.descriptions[l].trim()]).filter(([, value]) => value !== '')
    );

    if (attForm.editId) {
      updateAtt.mutate(
        { fundamentalId: currentFund.id, attitudeId: attForm.editId, names, descriptions },
        { onSuccess: () => setAttForm(f => ({ ...f, open: false })) }
      );
    } else {
      createAtt.mutate(
        { fundamentalId: currentFund.id, names, descriptions },
        {
          onSuccess: created => {
            setAttForm(f => ({ ...f, open: false }));
            setSelectedAttitude(created.id);
          },
        }
      );
    }
  }

  const isSavingFund = createFund.isPending || updateFund.isPending;
  const isSavingBeh = createHabit.isPending || updateHabit.isPending;
  const isSavingAtt = createAtt.isPending || updateAtt.isPending;

  return (
    <>
      {/* Confirm: eliminar fundamental */}
      {deleteFund && (
        <ConfirmModal
          type="danger"
          title={t('admin.fundDeleteTitle')}
          message={t('admin.fundDeleteDesc', { name: deleteFund.name })}
          confirmText={t('common.delete')}
          onConfirm={() => {
            deleteFundMut.mutate(deleteFund.id, {
              onSuccess: () => {
                setDeleteFund(null);
                if (selectedFund === deleteFund.id) setSelectedFund('');
              },
            });
          }}
          onClose={() => setDeleteFund(null)}
        />
      )}

      {/* Confirm: eliminar daily habit */}
      {deleteBeh && (
        <ConfirmModal
          type="danger"
          title={t('admin.behDeleteTitle')}
          message={t('admin.behDeleteDesc', { name: deleteBeh.name })}
          confirmText={t('common.delete')}
          onConfirm={() => {
            deleteHabitMut.mutate(
              { fundamentalId: deleteBeh.fundamentalId ?? effectiveSelected, habitId: deleteBeh.id },
              { onSuccess: () => setDeleteBeh(null) }
            );
          }}
          onClose={() => setDeleteBeh(null)}
        />
      )}

      {/* Confirm: eliminar actitud y comportamiento */}
      {deleteAtt && (
        <ConfirmModal
          type="danger"
          title={t('admin.attDeleteTitle')}
          message={t('admin.attDeleteDesc', { name: deleteAtt.name })}
          confirmText={t('common.delete')}
          onConfirm={() => {
            deleteAttMut.mutate(
              { fundamentalId: deleteAtt.fundamentalId ?? effectiveSelected, attitudeId: deleteAtt.id },
              {
                onSuccess: () => {
                  setDeleteAtt(null);
                  if (selectedAttitude === deleteAtt.id) setSelectedAttitude('');
                },
              }
            );
          }}
          onClose={() => setDeleteAtt(null)}
        />
      )}

      {/* Icon picker for fundamental modal */}
      {showFundIconPicker && (
        <IconPickerModal
          currentIcon={fundForm.icon}
          onSelect={v => setFundForm(f => ({ ...f, icon: v }))}
          onClose={() => setShowFundIconPicker(false)}
        />
      )}

      {/* Modal: Fundamental */}
      {fundForm.open && (
        <div className="funcat-modal-overlay" onClick={() => setFundForm(f => ({ ...f, open: false }))}>
          <div className="funcat-modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="funcat-modal-header">
              <h2><i className="fas fa-cubes" /> {fundForm.editId ? t('admin.fundEditTitle') : t('admin.fundNewTitle')}</h2>
              <button className="btn btn-outline btn-small" onClick={() => setFundForm(f => ({ ...f, open: false }))}><i className="fas fa-times" /></button>
            </div>
            <div className="funcat-modal-body">
              {/* Selector de idioma — controla los 3 campos de texto */}
              <div className="form-group" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{t('admin.fundFormLanguage')}</span>
                  <span style={{ fontSize: '0.72rem', color: allFundLangsFilled ? 'var(--cp-green, #20B15A)' : 'var(--text-muted)', fontWeight: 600 }}>
                    {LANG_KEYS.filter(l => fundForm.names[l].trim() !== '' && fundForm.shortNames[l].trim() !== '' && fundForm.descriptions[l].trim() !== '').length} / 7
                  </span>
                </div>
                <LangBar active={fundForm.activeLang} onSwitch={l => setFundForm(f => ({ ...f, activeLang: l }))} group="fund-lang" />
              </div>

              {/* Nombre completo */}
              <div className="form-group" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <label className="form-label" style={{ margin: 0 }}>{t('admin.fundFormFullName')} <span style={{ color: 'var(--cp-red, #E11F26)' }}>*</span></label>
                  <span style={{ fontSize: '0.72rem', color: fundNamesAllFilled ? 'var(--cp-green, #20B15A)' : 'var(--text-muted)', fontWeight: 600 }}>
                    {LANG_KEYS.filter(l => fundForm.names[l].trim() !== '').length} / 7
                  </span>
                </div>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type="text" placeholder={t('admin.fundFormFullNamePlaceholder')}
                    value={fundForm.names[fundForm.activeLang]}
                    onChange={e => setFundForm(f => ({ ...f, names: { ...f.names, [f.activeLang]: e.target.value } }))} />
                  {fundForm.names[fundForm.activeLang].trim() !== '' && (
                    <i className="fas fa-check" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--cp-green, #20B15A)', fontSize: '0.8rem', pointerEvents: 'none' }} />
                  )}
                </div>
              </div>

              {/* Nombre corto */}
              <div className="form-group" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <label className="form-label" style={{ margin: 0 }}>{t('admin.fundFormShortName')} <span style={{ color: 'var(--cp-red, #E11F26)' }}>*</span></label>
                  <span style={{ fontSize: '0.72rem', color: fundShortNamesAllFilled ? 'var(--cp-green, #20B15A)' : 'var(--text-muted)', fontWeight: 600 }}>
                    {LANG_KEYS.filter(l => fundForm.shortNames[l].trim() !== '').length} / 7
                  </span>
                </div>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type="text" placeholder={t('admin.fundFormShortNamePlaceholder')}
                    value={fundForm.shortNames[fundForm.activeLang]}
                    onChange={e => setFundForm(f => ({ ...f, shortNames: { ...f.shortNames, [f.activeLang]: e.target.value } }))} />
                  {fundForm.shortNames[fundForm.activeLang].trim() !== '' && (
                    <i className="fas fa-check" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--cp-green, #20B15A)', fontSize: '0.8rem', pointerEvents: 'none' }} />
                  )}
                </div>
              </div>

              {/* Descripción */}
              <div className="form-group" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <label className="form-label" style={{ margin: 0 }}>{t('admin.fundFormDescription')} <span style={{ color: 'var(--cp-red, #E11F26)' }}>*</span></label>
                  <span style={{ fontSize: '0.72rem', color: fundDescriptionsAllFilled ? 'var(--cp-green, #20B15A)' : 'var(--text-muted)', fontWeight: 600 }}>
                    {LANG_KEYS.filter(l => fundForm.descriptions[l].trim() !== '').length} / 7
                  </span>
                </div>
                <textarea className="form-input" rows={3} placeholder={t('admin.fundFormDescriptionPlaceholder')}
                  value={fundForm.descriptions[fundForm.activeLang]}
                  onChange={e => setFundForm(f => ({ ...f, descriptions: { ...f.descriptions, [f.activeLang]: e.target.value } }))}
                  style={{ resize: 'vertical', minHeight: 72 }} />
              </div>

              {!allFundLangsFilled && (
                <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  <i className="fas fa-info-circle" style={{ marginRight: 4 }} />{t('admin.fundFormAllLangsRequired')}
                </p>
              )}

              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">
                  {t('admin.fundFormIcon')} <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.75rem' }}>{t('admin.builderOptional')}</span>
                </label>
                <div className="icp-selected-row">
                  {fundForm.icon
                    ? <div className="icp-selected-thumb">
                        {fundForm.icon.startsWith('fa-')
                          ? <i className={`fas ${fundForm.icon}`} style={{ fontSize: 22 }} />
                          : <img src={fundForm.icon} alt={t('admin.fundFormIcon')} />
                        }
                      </div>
                    : <div className="icp-selected-thumb icp-selected-none"><i className="fas fa-ban" /></div>
                  }
                  <div className="icp-selected-info">
                    <span className="icp-selected-name">
                      {fundForm.icon
                        ? (BUILT_IN_ICONS.find(b => b.value === fundForm.icon)?.label ?? t('admin.builderCustomIcon'))
                        : t('admin.builderNoIcon')}
                    </span>
                    {fundForm.icon && (
                      <span className="icp-selected-badge"><i className="fas fa-check" /> {t('admin.builderSelectedIcon')}</span>
                    )}
                  </div>
                  <button type="button" className="btn-small btn-secondary-sm" onClick={() => setShowFundIconPicker(true)}>
                    <i className="fas fa-images" />{' '}
                    {fundForm.icon ? t('admin.builderChangeIcon') : t('admin.builderSelectIconBtn')}
                  </button>
                </div>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">{t('admin.fundFormColor')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <input type="color" value={fundForm.color}
                    style={{ width: 44, height: 36, border: '1.5px solid rgba(52,47,130,0.15)', borderRadius: 8, padding: 2, cursor: 'pointer', background: 'white' }}
                    onChange={e => setFundForm(f => ({ ...f, color: e.target.value }))} />
                  <div className="funcat-color-presets">
                    {COLOR_PRESETS.map(c => (
                      <button key={c} type="button" className="funcat-color-preset" style={{ background: c }}
                        onClick={() => setFundForm(f => ({ ...f, color: c }))} title={c} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">
                  {t('admin.fundFormUrl')} <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.75rem' }}>{t('admin.builderOptional')}</span>
                </label>
                <input className="form-input" type="url" placeholder={t('admin.fundFormUrlPlaceholder')}
                  value={fundForm.url} onChange={e => setFundForm(f => ({ ...f, url: e.target.value }))} />
              </div>
              {fundForm.editId && (
                <div className="form-group" style={{ margin: 0 }}>
                  <CatalogImageUploadField
                    label={t('admin.fundFormImage')}
                    currentImageUrl={fundamentals.find(f => f.id === fundForm.editId)?.image}
                    isUploading={uploadFundImage.isPending}
                    onUpload={file => uploadFundImage.mutateAsync({ fundamentalId: fundForm.editId as string, file })}
                  />
                </div>
              )}
            </div>
            <div className="funcat-modal-footer">
              <button className="btn btn-outline" onClick={() => setFundForm(f => ({ ...f, open: false }))} disabled={isSavingFund}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSaveFund} disabled={isSavingFund || !allFundLangsFilled}>
                {isSavingFund ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />} {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Attitude and Behavior */}
      {attForm.open && currentFund && (
        <div className="funcat-modal-overlay" onClick={() => setAttForm(f => ({ ...f, open: false }))}>
          <div className="funcat-modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="funcat-modal-header">
              <h2><i className="fas fa-layer-group" /> {attForm.editId ? t('admin.attEditTitle') : t('admin.attNewTitle')}</h2>
              <button className="btn btn-outline btn-small" onClick={() => setAttForm(f => ({ ...f, open: false }))}><i className="fas fa-times" /></button>
            </div>
            <div className="funcat-modal-body">
              <div className="form-group" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label className="form-label" style={{ margin: 0 }}>
                    {t('admin.attFormName')} <span style={{ color: 'var(--cp-red, #E11F26)' }}>*</span>
                  </label>
                  <span style={{ fontSize: '0.72rem', color: attAllLangsFilled ? 'var(--cp-green, #20B15A)' : 'var(--text-muted)', fontWeight: 600 }}>
                    {LANG_KEYS.filter(l => attForm.names[l].trim() !== '').length} / 7
                  </span>
                </div>
                <LangBar active={attForm.activeLang} onSwitch={l => setAttForm(f => ({ ...f, activeLang: l }))} group="att-name" />
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type="text" placeholder={t('admin.attFormNamePlaceholder')}
                    value={attForm.names[attForm.activeLang]}
                    onChange={e => setAttForm(f => ({ ...f, names: { ...f.names, [f.activeLang]: e.target.value } }))} />
                  {attForm.names[attForm.activeLang].trim() !== '' && (
                    <i className="fas fa-check" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--cp-green, #20B15A)', fontSize: '0.8rem', pointerEvents: 'none' }} />
                  )}
                </div>
                {!attAllLangsFilled && (
                  <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    <i className="fas fa-info-circle" style={{ marginRight: 4 }} />{t('admin.attFormAllLangsRequired')}
                  </p>
                )}
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ margin: 0, marginBottom: 4, display: 'block' }}>{t('admin.attFormDescription')}</label>
                <textarea className="form-input" rows={3} placeholder={t('admin.attFormDescriptionPlaceholder')}
                  value={attForm.descriptions[attForm.activeLang]}
                  onChange={e => setAttForm(f => ({ ...f, descriptions: { ...f.descriptions, [f.activeLang]: e.target.value } }))}
                  style={{ resize: 'vertical', minHeight: 72 }} />
              </div>
              {attForm.editId && (
                <div className="form-group" style={{ margin: 0 }}>
                  <CatalogImageUploadField
                    label={t('admin.attFormImage')}
                    currentImageUrl={currentFund.attitudes.find(a => a.id === attForm.editId)?.image}
                    isUploading={uploadAttImage.isPending}
                    onUpload={file => uploadAttImage.mutateAsync({ fundamentalId: currentFund.id, attitudeId: attForm.editId as string, file })}
                  />
                </div>
              )}
            </div>
            <div className="funcat-modal-footer">
              <button className="btn btn-outline" onClick={() => setAttForm(f => ({ ...f, open: false }))} disabled={isSavingAtt}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSaveAtt} disabled={isSavingAtt || !attAllLangsFilled}>
                {isSavingAtt ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />} {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Daily Habit */}
      {behForm.open && (
        <div className="funcat-modal-overlay" onClick={() => setBehForm(f => ({ ...f, open: false }))}>
          <div className="funcat-modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 580 }}>
            <div className="funcat-modal-header">
              <h2><i className="fas fa-shapes" /> {behForm.editId ? t('admin.behEditTitle') : t('admin.behNewTitle')}</h2>
              <button className="btn btn-outline btn-small" onClick={() => setBehForm(f => ({ ...f, open: false }))}><i className="fas fa-times" /></button>
            </div>
            <div className="funcat-modal-body">
              {/* Nombre multiidioma */}
              <div className="form-group" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label className="form-label" style={{ margin: 0 }}>
                    {t('admin.behFormName')} <span style={{ color: 'var(--cp-red, #E11F26)' }}>*</span>
                  </label>
                  <span style={{ fontSize: '0.72rem', color: allLangsFilled ? 'var(--cp-green, #20B15A)' : 'var(--text-muted)', fontWeight: 600 }}>
                    {LANG_KEYS.filter(l => behForm.names[l].trim() !== '').length} / 7
                  </span>
                </div>
                <LangBar
                  active={behForm.activeLang}
                  onSwitch={l => setBehForm(f => ({ ...f, activeLang: l }))}
                  group="beh-name"
                />
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    type="text"
                    placeholder={t('admin.behFormNamePlaceholder')}
                    value={behForm.names[behForm.activeLang]}
                    onChange={e => setBehForm(f => ({
                      ...f,
                      names: { ...f.names, [f.activeLang]: e.target.value },
                    }))}
                  />
                  {behForm.names[behForm.activeLang].trim() !== '' && (
                    <i className="fas fa-check" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--cp-green, #20B15A)', fontSize: '0.8rem', pointerEvents: 'none' }} />
                  )}
                </div>
                {!allLangsFilled && (
                  <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    <i className="fas fa-info-circle" style={{ marginRight: 4 }} />{t('admin.behFormAllLangsRequired')}
                  </p>
                )}
              </div>
              {/* Fundamental vinculado */}
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">{t('admin.behFormFundamental')}</label>
                <select className="form-input" value={behForm.fund} onChange={e => setBehForm(f => ({ ...f, fund: e.target.value }))}>
                  <option value="">{t('admin.tipFormSelectFundamental')}</option>
                  {fundamentals.map(f => <option key={f.id} value={f.id}>{getFundamentalName(f, lang)}</option>)}
                </select>
              </div>
              {behForm.editId && (
                <div className="form-group" style={{ margin: 0 }}>
                  <CatalogImageUploadField
                    label={t('admin.fundFormImage')}
                    currentImageUrl={(currentFund?.dailyHabits ?? []).find(h => h.id === behForm.editId)?.image}
                    isUploading={uploadHabitImage.isPending}
                    onUpload={file => uploadHabitImage.mutateAsync({ fundamentalId: behForm.fund, habitId: behForm.editId as string, file })}
                  />
                </div>
              )}
            </div>
            <div className="funcat-modal-footer">
              <button className="btn btn-outline" onClick={() => setBehForm(f => ({ ...f, open: false }))} disabled={isSavingBeh}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSaveBeh}
                disabled={isSavingBeh || !allLangsFilled || !behForm.fund}>
                {isSavingBeh ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />} {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-header">
        <h2><i className="fas fa-cubes" style={{ color: '#342F82' }} /> {t('admin.catalogTitle')}</h2>
        <button className="btn btn-primary" onClick={() => openFundForm()}>
          <i className="fas fa-plus" /> {t('admin.fundNewTitle')}
        </button>
      </div>

      <div className="funcat-layout">
        <div className={`funcat-panel-left${mobileStage === 'funds' ? ' is-mobile-active' : ''}`}>
          <div className="funcat-search-wrap">
            <i className="fas fa-search funcat-search-icon" />
            <input className="funcat-search-input" type="text" placeholder={t('admin.catalogSearchFundamental')}
              value={fundSearch} onChange={e => setFundSearch(e.target.value)} autoComplete="off" />
          </div>
          <div className="funcat-fund-list">
            {loadingFundamentals
              ? <div className="tips-empty" style={{ padding: '32px 0' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: '1.4rem', color: 'var(--cp-purple, #342F82)' }} /></div>
              : filteredFunds.length === 0
              ? <div className="tips-empty" style={{ padding: '32px 0' }}><i className="fas fa-inbox" /><p>{t('admin.catalogNoFundamentals')}</p></div>
              : filteredFunds.map(f => {
                const posInFullList = sortedFunds.findIndex(x => x.id === f.id);
                const isFirst = posInFullList === 0;
                const isLast = posInFullList === sortedFunds.length - 1;
                return (
                <div key={f.id} className={`funcat-fund-item${effectiveSelected === f.id ? ' active' : ''}`}
                  onClick={() => { setSelectedFund(f.id); setBehSearch(''); setSelectedAttitude(''); setMobileStage('mid'); }}>
                  <span className="funcat-fund-dot" style={{ background: f.color ?? '#342F82' }} />
                  <div className="funcat-fund-info">
                    <div className="funcat-fund-name">{getFundamentalName(f, lang)}</div>
                    <div className="funcat-fund-meta">{f.dailyHabits.length} daily habits</div>
                  </div>
                  <div className="funcat-fund-actions">
                    <button className="funcat-icon-btn" title={t('common.moveUp')} disabled={isFirst || moveFund.isPending}
                      onClick={e => { e.stopPropagation(); moveFund.mutate({ id: f.id, direction: 'up' }); }}>
                      <i className="fas fa-arrow-up" />
                    </button>
                    <button className="funcat-icon-btn" title={t('common.moveDown')} disabled={isLast || moveFund.isPending}
                      onClick={e => { e.stopPropagation(); moveFund.mutate({ id: f.id, direction: 'down' }); }}>
                      <i className="fas fa-arrow-down" />
                    </button>
                    <button className="funcat-icon-btn" title={t('common.edit')} onClick={e => { e.stopPropagation(); openFundForm(f.id); }}>
                      <i className="fas fa-pen" />
                    </button>
                    <button
                      className="funcat-icon-btn funcat-icon-danger"
                      title={t('common.delete')}
                      onClick={e => { e.stopPropagation(); setDeleteFund({ id: f.id, name: getFundamentalName(f, lang) }); }}
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                </div>
                );
              })
            }
          </div>
        </div>

        <div className="funcat-panel-right">
          {loadingFundamentals ? (
            <div className="funcat-empty" style={{ height: '100%', justifyContent: 'center' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.4rem', color: 'var(--cp-purple, #342F82)' }} />
            </div>
          ) : !currentFund ? (
            <div className="funcat-empty" style={{ height: '100%', justifyContent: 'center' }}>
              <i className="fas fa-arrow-left" /><p>{t('admin.catalogSelectFundamental')}</p>
            </div>
          ) : hasAttitudes ? (
            <div className="funcat-attitude-split">
              <div className={`funcat-panel-mid${mobileStage === 'mid' ? ' is-mobile-active' : ''}`}>
                <div className="funcat-beh-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span className="funcat-fund-dot" style={{ background: currentFund.color ?? '#342F82', width: 12, height: 12 }} />
                    <strong style={{ color: 'var(--text-primary)' }}>{getFundamentalName(currentFund, lang)}</strong>
                    <span className="funcat-beh-badge">{currentFund.attitudes.length} {t('admin.catalogAttitudesLabel')}</span>
                  </div>
                  <button className="btn btn-primary btn-small" onClick={() => openAttForm()}>
                    <i className="fas fa-plus" /> {t('admin.attNewTitle')}
                  </button>
                </div>
                <div className="funcat-fund-list">
                  {sortedAttitudes.map((a, index) => (
                    <div
                      key={a.id}
                      className={`funcat-fund-item${effectiveSelectedAttitude === a.id ? ' active' : ''}`}
                      onClick={() => { setSelectedAttitude(a.id); setMobileStage('detail'); }}
                    >
                      <span className="funcat-fund-dot" style={{ background: currentFund.color ?? '#342F82' }} />
                      <div className="funcat-fund-info">
                        <div className="funcat-fund-name">{getAttitudeName(a, lang)}</div>
                        <div className="funcat-fund-meta">
                          {currentFund.dailyHabits.filter(h => h.attitudeBehaviorId === a.id).length} daily habits
                        </div>
                      </div>
                      <div className="funcat-fund-actions">
                        <button className="funcat-icon-btn" title={t('common.moveUp')} disabled={index === 0 || moveAtt.isPending}
                          onClick={e => { e.stopPropagation(); moveAtt.mutate({ fundamentalId: currentFund.id, attitudeId: a.id, direction: 'up' }); }}>
                          <i className="fas fa-arrow-up" />
                        </button>
                        <button className="funcat-icon-btn" title={t('common.moveDown')} disabled={index === sortedAttitudes.length - 1 || moveAtt.isPending}
                          onClick={e => { e.stopPropagation(); moveAtt.mutate({ fundamentalId: currentFund.id, attitudeId: a.id, direction: 'down' }); }}>
                          <i className="fas fa-arrow-down" />
                        </button>
                        <button className="funcat-icon-btn" title={t('common.edit')} onClick={e => { e.stopPropagation(); openAttForm(a.id); }}>
                          <i className="fas fa-pen" />
                        </button>
                        <button
                          className="funcat-icon-btn funcat-icon-danger"
                          title={t('common.delete')}
                          onClick={e => { e.stopPropagation(); setDeleteAtt({ id: a.id, name: getAttitudeName(a, lang), fundamentalId: currentFund.id }); }}
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button type="button" className="funcat-mobile-back" onClick={() => setMobileStage('funds')}>
                  <i className="fas fa-arrow-left" /> {t('admin.catalogBackToFundamentals')}
                </button>
              </div>

              <div className={`funcat-panel-detail${mobileStage === 'detail' ? ' is-mobile-active' : ''}`}>
                {currentAttitude ? (
                  <>
                    <div className="funcat-beh-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span className="funcat-fund-dot" style={{ background: currentFund.color ?? '#342F82', width: 12, height: 12 }} />
                        <strong style={{ color: 'var(--text-primary)' }}>{getAttitudeName(currentAttitude, lang)}</strong>
                        <span className="funcat-beh-badge">{attitudeHabits.length} daily habits</span>
                      </div>
                      <button className="btn btn-primary btn-small" onClick={() => openBehForm()}>
                        <i className="fas fa-plus" /> {t('admin.behNewTitle')}
                      </button>
                    </div>
                    {attitudeHabits.length === 0 ? (
                      <div className="funcat-empty" style={{ padding: '48px 0' }}>
                        <i className="fas fa-shapes" /><p>{t('admin.catalogNoBehaviors')}</p>
                        <button className="btn btn-primary btn-small" style={{ marginTop: 8 }} onClick={() => openBehForm()}>
                          <i className="fas fa-plus" /> {t('admin.catalogAddBehavior')}
                        </button>
                      </div>
                    ) : (
                      <div className="funcat-beh-list">
                        {attitudeHabits.map((b, index) => (
                          <div key={b.id} className="funcat-beh-item">
                            <span className="funcat-beh-dot" style={{ background: currentFund.color ?? '#342F82' }} />
                            <div className="funcat-beh-content">
                              <div className="funcat-beh-name">{getHabitName(b, lang)}</div>
                              {b.questionIntro[lang] && <div className="funcat-beh-intro">{b.questionIntro[lang]}</div>}
                            </div>
                            <div className="funcat-beh-actions">
                              <button className="funcat-icon-btn" title={t('common.moveUp')} disabled={index === 0 || moveHabit.isPending}
                                onClick={() => moveHabit.mutate({ fundamentalId: currentFund.id, habitId: b.id, direction: 'up' })}>
                                <i className="fas fa-arrow-up" />
                              </button>
                              <button className="funcat-icon-btn" title={t('common.moveDown')} disabled={index === attitudeHabits.length - 1 || moveHabit.isPending}
                                onClick={() => moveHabit.mutate({ fundamentalId: currentFund.id, habitId: b.id, direction: 'down' })}>
                                <i className="fas fa-arrow-down" />
                              </button>
                              <button className="funcat-icon-btn" title={t('common.edit')} onClick={() => openBehForm(b.id)}><i className="fas fa-pen" /></button>
                              <button
                                className="funcat-icon-btn funcat-icon-danger"
                                title={t('common.delete')}
                                onClick={() => setDeleteBeh({ id: b.id, name: getHabitName(b, lang), fundamentalId: currentFund.id })}
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="funcat-empty" style={{ height: '100%', justifyContent: 'center' }}>
                    <i className="fas fa-arrow-left" /><p>{t('admin.catalogSelectAttitude')}</p>
                  </div>
                )}
                <button type="button" className="funcat-mobile-back" onClick={() => setMobileStage('mid')}>
                  <i className="fas fa-arrow-left" /> {t('admin.catalogBackToAttitudes')}
                </button>
              </div>
            </div>
          ) : (
            <div className={`funcat-panel-flat${mobileStage === 'mid' ? ' is-mobile-active' : ''}`}>
              <div className="funcat-beh-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="funcat-fund-dot" style={{ background: currentFund.color ?? '#342F82', width: 12, height: 12 }} />
                  <strong style={{ color: 'var(--text-primary)' }}>{getFundamentalName(currentFund, lang)}</strong>
                  <span className="funcat-beh-badge">{filteredDirectHabits.length} daily habits</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div className="funcat-search-wrap" style={{ width: 170 }}>
                    <i className="fas fa-search funcat-search-icon" />
                    <input className="funcat-search-input" type="text" placeholder={t('admin.catalogSearchBehavior')}
                      value={behSearch} onChange={e => setBehSearch(e.target.value)} autoComplete="off" />
                  </div>
                  {currentFund.dailyHabits.length === 0 && (
                    <button className="btn btn-outline btn-small" onClick={() => openAttForm()}>
                      <i className="fas fa-layer-group" /> {t('admin.catalogGroupWithAttitudes')}
                    </button>
                  )}
                  <button className="btn btn-primary btn-small" onClick={() => openBehForm()}>
                    <i className="fas fa-plus" /> {t('admin.behNewTitle')}
                  </button>
                </div>
              </div>
              {filteredDirectHabits.length === 0 ? (
                <div className="funcat-empty" style={{ padding: '48px 0' }}>
                  <i className="fas fa-shapes" /><p>{t('admin.catalogNoBehaviors')}</p>
                  <button className="btn btn-primary btn-small" style={{ marginTop: 8 }} onClick={() => openBehForm()}>
                    <i className="fas fa-plus" /> {t('admin.catalogAddBehavior')}
                  </button>
                </div>
              ) : (
                <div className="funcat-beh-list">
                  {filteredDirectHabits.map((b, index) => (
                    <div key={b.id} className="funcat-beh-item">
                      <span className="funcat-beh-dot" style={{ background: currentFund.color ?? '#342F82' }} />
                      <div className="funcat-beh-content">
                        <div className="funcat-beh-name">{getHabitName(b, lang)}</div>
                        {b.questionIntro[lang] && <div className="funcat-beh-intro">{b.questionIntro[lang]}</div>}
                      </div>
                      <div className="funcat-beh-actions">
                        <button className="funcat-icon-btn" title={t('common.moveUp')} disabled={index === 0 || moveHabit.isPending}
                          onClick={() => moveHabit.mutate({ fundamentalId: currentFund.id, habitId: b.id, direction: 'up' })}>
                          <i className="fas fa-arrow-up" />
                        </button>
                        <button className="funcat-icon-btn" title={t('common.moveDown')} disabled={index === filteredDirectHabits.length - 1 || moveHabit.isPending}
                          onClick={() => moveHabit.mutate({ fundamentalId: currentFund.id, habitId: b.id, direction: 'down' })}>
                          <i className="fas fa-arrow-down" />
                        </button>
                        <button className="funcat-icon-btn" title={t('common.edit')} onClick={() => openBehForm(b.id)}><i className="fas fa-pen" /></button>
                        <button
                          className="funcat-icon-btn funcat-icon-danger"
                          title={t('common.delete')}
                          onClick={() => setDeleteBeh({ id: b.id, name: getHabitName(b, lang), fundamentalId: currentFund.id })}
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" className="funcat-mobile-back" onClick={() => setMobileStage('funds')}>
                <i className="fas fa-arrow-left" /> {t('admin.catalogBackToFundamentals')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <MadurezEditor />
      </div>

      <div style={{ marginTop: 16 }}>
        <NivelesAdopcionEditor />
      </div>
    </>
  );
}
